import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    UserPlus,
    Clock,
    UserCog,
    DollarSign,
    Calendar,
    Briefcase,
    FileText,
    CheckCircle,
    XCircle,
    ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ProviderCreateDialog = ({ open, onOpenChange, onSuccess }) => {
    const { addStaffPreRegistration } = useAuth();

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Form State
    const [discordId, setDiscordId] = useState('');
    const [accessLevel, setAccessLevel] = useState('suporte');
    const [contractValue, setContractValue] = useState('');
    const [contractDuration, setContractDuration] = useState('');
    const [contractUnit, setContractUnit] = useState('months');
    const [codename, setCodename] = useState('');
    const [serviceDescription, setServiceDescription] = useState('');

    const handlePreRegister = async (e) => {
        e.preventDefault();
        if (!discordId) return;

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await addStaffPreRegistration(
                discordId,
                accessLevel,
                contractValue,
                contractDuration,
                codename,
                serviceDescription,
                contractUnit
            );

            if (result.success) {
                setMessage({ type: 'success', text: 'Convite criado com sucesso!' });

                // Reset form
                setDiscordId('');
                setContractValue('');
                setContractDuration('');
                setContractUnit('months');
                setCodename('');
                setServiceDescription('');
                setAccessLevel('suporte');

                if (onSuccess) onSuccess();

                // Close after a short delay to show success message
                setTimeout(() => {
                    setMessage({ type: '', text: '' });
                    onOpenChange(false);
                }, 1500);

            } else {
                setMessage({ type: 'error', text: result.error || 'Erro ao criar convite.' });
            }
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Erro interno ao processar.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#09090b] border border-white/10 text-white sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-purple-500" />
                        Novo Contrato de Staff
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                        Preencha os dados para gerar um convite de cadastro.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handlePreRegister} className="space-y-4 py-2">
                    {/* Identificação */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                            <UserCog className="w-3 h-3" /> Identificação
                        </label>
                        <div className="grid gap-3">
                            <Input
                                placeholder="ID do Discord (Ex: 401033...)"
                                value={discordId}
                                onChange={(e) => setDiscordId(e.target.value)}
                                className="bg-white/5 border-white/10 text-white focus:border-purple-500/50"
                                required
                            />
                            <Input
                                placeholder="Codinome (Opcional)"
                                value={codename}
                                onChange={(e) => setCodename(e.target.value)}
                                className="bg-white/5 border-white/10 text-white focus:border-purple-500/50"
                            />
                        </div>
                    </div>

                    {/* Financeiro */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                            <DollarSign className="w-3 h-3" /> Financeiro & Prazos
                        </label>
                        <div className="grid gap-3">
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
                                <Input
                                    placeholder="Valor Mensal"
                                    value={contractValue}
                                    onChange={(e) => setContractValue(e.target.value)}
                                    className="bg-white/5 border-white/10 text-white pl-9 focus:border-green-500/50"
                                />
                            </div>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
                                    <Input
                                        type="number"
                                        placeholder="Duração"
                                        value={contractDuration}
                                        onChange={(e) => setContractDuration(e.target.value)}
                                        className="bg-white/5 border-white/10 text-white pl-9 focus:border-blue-500/50"
                                    />
                                </div>
                                <Select value={contractUnit} onValueChange={setContractUnit}>
                                    <SelectTrigger className="w-[100px] bg-white/5 border-white/10 text-white focus:border-blue-500/50">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                        <SelectItem value="months">Meses</SelectItem>
                                        <SelectItem value="days">Dias</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Função */}
                    <div className="space-y-3">
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                            <Briefcase className="w-3 h-3" /> Função & Detalhes
                        </label>
                        <Select value={accessLevel} onValueChange={setAccessLevel}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white focus:border-orange-500/50">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10 text-white max-h-[200px]">
                                <SelectItem value="suporte">Suporte</SelectItem>
                                <SelectItem value="suporte_compras">Suporte Compras</SelectItem>
                                <SelectItem value="suporte_descontos">Suporte Descontos</SelectItem>
                                <SelectItem value="moderador">Moderador</SelectItem>
                                <SelectItem value="super_moderador">Super Moderador</SelectItem>
                                <SelectItem value="programador">Programador</SelectItem>
                                <SelectItem value="head_suporte">Head Suporte</SelectItem>
                                <SelectItem value="head_moderacao">Head Moderação</SelectItem>
                                <SelectItem value="head_staff">Head Staff</SelectItem>
                                <SelectItem value="analista_qualidade">Analista de Qualidade</SelectItem>
                                <SelectItem value="departamento_juridico">Departamento Jurídico</SelectItem>
                                <SelectItem value="criador_de_conteudo">Criador de Conteúdo</SelectItem>
                                <SelectItem value="admin">Administrador</SelectItem>
                            </SelectContent>
                        </Select>

                        <textarea
                            placeholder="Descrição do serviço (Ex: Suporte técnico...)"
                            value={serviceDescription}
                            onChange={(e) => setServiceDescription(e.target.value)}
                            className="w-full h-20 bg-white/5 border border-white/10 rounded-md p-3 text-sm text-white focus:outline-none focus:border-purple-500/50 resize-none"
                        />
                    </div>

                    {/* Actions */}
                    <DialogFooter className="pt-2 gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            className="border-white/10 hover:bg-white/5 text-gray-300"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading || !discordId}
                            className="bg-purple-600 hover:bg-purple-700 text-white min-w-[140px]"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 animate-spin" /> Processando
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    Gerar Convite <ArrowRight className="w-4 h-4" />
                                </span>
                            )}
                        </Button>
                    </DialogFooter>

                    <AnimatePresence>
                        {message.text && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                <Alert className={`mt-2 ${message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                                    <AlertDescription className="flex items-center gap-2 text-xs">
                                        {message.type === 'success' ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                        {message.text}
                                    </AlertDescription>
                                </Alert>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ProviderCreateDialog;
